<template>
  <div class="flex-1">
    <router-view v-slot="{ Component, route }">
      <Suspense timeout="0" @pending="emitPendingChange(true)" @resolve="emitPendingChange(false)">
        <template #default>
          <component
            :is="Component"
            v-if="Component"
            :key="routeViewKey"
            :class="getViewContainerClass(route.name, route.meta?.layout)"
          />
        </template>
        <template #fallback>
          <div
            :key="`fallback-${routeViewKey}`"
            :class="[
              getViewContainerClass(route.name, route.meta?.layout),
              'flex min-h-[320px] items-center justify-center'
            ]"
          >
            <LoadingSpinner :message="translate('common.loading')" size="large" />
          </div>
        </template>
      </Suspense>
    </router-view>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import LoadingSpinner from "@core/components/shared/LoadingSpinner.vue";
import { useNavigation, type ViewType } from "@core/composables/state/useNavigation";
import { useRouteContext } from "@core/composables/state/useRouteContext";

import { useTranslate } from "@common/composables/useTranslate";

const props = defineProps<{
  centeredLayout: boolean;
}>();

const emit = defineEmits<{
  (event: "pending-change", value: boolean): void;
}>();

const routeContext = useRouteContext();
const { viewKey } = useNavigation();
const translate = useTranslate();

const routeViewKey = computed(() => {
  const viewGroup = routeContext.meta.viewGroup;
  if (viewGroup) {
    const params = routeContext.meta.viewGroupParams ?? [];
    const paramsKey = params
      .map(p => routeContext.params[p])
      .filter(Boolean)
      .join("-");
    return [viewGroup, paramsKey, viewKey.value].filter(Boolean).join("-");
  }
  const routeName = typeof routeContext.name === "string" ? routeContext.name : "view";
  return `${routeContext.path || routeName}-${viewKey.value}`;
});

const emitPendingChange = (value: boolean) => {
  emit("pending-change", value);
};

const fullWidthViewNames: ViewType[] = [
  "payment",
  "add-vehicle",
  "edit-vehicle",
  "add-payment-source",
  "edit-payment-source",
  "statistics",
  "cameras-overview",
  "applications",
  "youpark-customer-admin-locations-settings",
  "youpark-customer-admin-locations-price-setup",
  "youpark-customer-admin-locations-autopass",
  "youpark-customer-admin-locations-display",
  "youpark-customer-admin-locations-contactpersons",
  "youpark-customer-admin-locations-cameras",
  "youpark-customer-admin-locations-connections",
  "youpark-customer-admin-locations-contract",
  "youpark-customer-admin-locations-businessmodel"
];
const fullWidthViewSet = new Set(fullWidthViewNames);

function getViewContainerClass(routeName: unknown, layout: unknown) {
  if (layout === "chrome-free") {
    return "min-h-screen w-full";
  }
  if (layout === "map") {
    return "h-full w-full";
  }
  const view = typeof routeName === "string" ? (routeName as ViewType) : null;
  const isFullWidthView = view !== null && fullWidthViewSet.has(view);

  if (isFullWidthView) {
    return "";
  }

  const alignmentClass = props.centeredLayout ? "mx-auto" : "mx-0";
  return `max-w-full xl:max-w-6xl p-4 md:p-8 ${alignmentClass}`;
}
</script>
